import React, { useState, useEffect } from 'react';
import { useRole, BACKEND_URL } from '../App';
import { Link } from 'react-router-dom';
import {
  StatusBadge, TypePill, TriggerPill, CardSkeleton, EmptyState,
  getKpiStatus, PageHeader, NotionBlockRenderer
} from '../components/shared';
import { GuidedProcedureView } from '../components/GuidedProcedure';
import { toast } from 'sonner';
import {
  FileText, BookOpen, AlertTriangle, TrendingUp, Check, Pencil, X,
  Activity, Zap, ChevronRight, ChevronDown, ChevronLeft, Shield, Eye, ExternalLink,
  Clock, MapPin, Heart, GraduationCap, Target, ArrowRight, Sparkles
} from 'lucide-react';

export default function HomeDashboard() {
  const { role, canWrite } = useRole();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingKpi, setEditingKpi] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [expandedActivity, setExpandedActivity] = useState(null);
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [selectedProcId, setSelectedProcId] = useState(null);
  const [procDetail, setProcDetail] = useState(null);
  const [procDetailLoading, setProcDetailLoading] = useState(false);
  
  // Collapsible sections state (persisted in localStorage)
  // Default: everything collapsed EXCEPT onboarding
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem('ps_collapsed');
      if (saved) return JSON.parse(saved);
      // First visit defaults
      return { roleContext: true, kpis: true, activities: true, procedures: true, quickActions: true, onboarding: false };
    } catch { return { roleContext: true, kpis: true, activities: true, procedures: true, quickActions: true, onboarding: false }; }
  });
  
  const toggleSection = (key) => {
    setCollapsed(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('ps_collapsed', JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${BACKEND_URL}/api/home?role=${encodeURIComponent(role)}`)
      .then(r => { if (!r.ok) throw new Error('Failed to load'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [role]);

  const handleKpiUpdate = async (kpiId, newValue) => {
    const numValue = parseFloat(newValue);
    if (isNaN(numValue)) { toast.error('Please enter a valid number'); return; }
    setData(prev => ({ ...prev, kpis: prev.kpis.map(k => k.id === kpiId ? { ...k, current_value: numValue } : k) }));
    setEditingKpi(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/kpis/${kpiId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: numValue, role }) });
      if (!res.ok) throw new Error();
      toast.success('KPI updated successfully');
    } catch { toast.error('Failed to update KPI'); }
  };

  const handleTrainingToggle = async (activityId, currentStatus) => {
    const newStatus = currentStatus === 'Complete' ? 'Not Started' : 'Complete';
    setData(prev => ({ ...prev, activities: prev.activities.map(a => a.id === activityId ? { ...a, training_status: newStatus } : a) }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/activities/${activityId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus, role }) });
      if (!res.ok) throw new Error();
      toast.success(`Training ${newStatus === 'Complete' ? 'completed' : 'reset'}`);
    } catch { toast.error('Failed to update training status'); }
  };

  const openProcedure = async (procId) => {
    setSelectedProcId(procId);
    setProcDetailLoading(true);
    setProcDetail(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/procedures/${procId}`);
      const data = await res.json();
      setProcDetail(data);
    } catch { }
    setProcDetailLoading(false);
  };

  const handlePhaseToggle = async (phaseNum, currentVerified) => {
    const newCompleted = !currentVerified;
    // Optimistic update
    setData(prev => {
      if (!prev?.onboarding?.phases) return prev;
      const newPhases = prev.onboarding.phases.map((p, i) =>
        i === phaseNum - 1 ? { ...p, status: newCompleted ? 'complete' : 'not_started', verified: newCompleted } : p
      );
      return { ...prev, onboarding: { ...prev.onboarding, phases: newPhases } };
    });
    // Also update team_training if present
    setData(prev => {
      if (!prev?.team_training) return prev;
      return prev;
    });
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/onboarding/phases`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phase: phaseNum, completed: newCompleted, role })
      });
      if (!res.ok) throw new Error();
      toast.success(`Phase ${phaseNum} ${newCompleted ? 'verified' : 'unverified'}`);
    } catch {
      toast.error('Failed to update phase');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="h-8 w-64 bg-muted rounded animate-skeleton mb-2" />
        <div className="h-4 w-48 bg-muted rounded animate-skeleton mb-8" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {[1,2,3].map(i => <CardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="ps-card-static p-8 text-center" data-testid="error-alert">
          <AlertTriangle size={40} className="mx-auto mb-4 text-destructive opacity-60" />
          <h3 className="text-lg font-semibold mb-2">Unable to load dashboard</h3>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-xl text-sm font-medium" style={{ background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }} data-testid="error-retry-button">Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return null;
  const { person, role_context, procedures, activities, kpis, is_onboarding, onboarding, team_training, crm_pipeline, crm_kpis: crmKpis, pet400 } = data;

  return (
    <div className="page-container" data-testid="home-dashboard">
      {/* Welcome Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
          Welcome back, {person.name.split(' ')[0]}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {person.position_title} &middot; {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      {/* Role Context Banner — collapsible */}
      {role_context && role_context.purpose && (
        <div className="ps-card-static mb-6 overflow-hidden" style={{ borderLeft: '4px solid hsl(var(--primary))' }} data-testid="role-context-banner">
          <button onClick={() => toggleSection('roleContext')} className="w-full p-5 flex items-center gap-3 text-left">
            <Shield size={20} className="text-primary flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-bold">{role_context.name}</h3>
            </div>
            <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-200 ${collapsed.roleContext ? '' : 'rotate-180'}`} />
          </button>
          {!collapsed.roleContext && (
            <div className="px-5 pb-5">
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">{role_context.purpose}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {role_context.owns && (
                  <div className="bg-[hsl(142_40%_97%)] rounded-xl p-4">
                    <p className="text-xs font-bold text-[hsl(142_34%_24%)] uppercase tracking-wider mb-2 flex items-center gap-1"><Check size={12} /> You Own</p>
                    <div className="text-xs text-[hsl(142_34%_30%)] space-y-1 leading-relaxed">
                      {role_context.owns.split('\n').filter(Boolean).map((line, i) => (
                        <p key={i}>{line.replace(/^[–-]\s*/, '').trim()}</p>
                      ))}
                    </div>
                  </div>
                )}
                {role_context.does_not_own && (
                  <div className="bg-[hsl(30_18%_95%)] rounded-xl p-4">
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1"><X size={12} /> You Don't Own</p>
                    <div className="text-xs text-muted-foreground space-y-1 leading-relaxed">
                      {role_context.does_not_own.split('\n').filter(Boolean).map((line, i) => (
                        <p key={i}>{line.replace(/^[–-]\s*/, '').trim()}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Team Training Management (Director/Ops Manager only) */}
      {canWrite && team_training && team_training.length > 0 && (
        <section className="mb-8" data-testid="team-training-section">
          <div className="flex items-center gap-2 mb-4">
            <GraduationCap size={20} className="text-[hsl(var(--info))]" />
            <h2 className="text-lg font-semibold">Team Training</h2>
          </div>
          {team_training.map(member => {
            const tp = member.onboarding?.training_progress;
            const progress = tp ? (tp.complete / Math.max(tp.total, 1)) * 100 : 0;
            return (
              <div key={member.person.id} className="ps-card-static overflow-hidden mb-4" style={{ borderLeft: '4px solid hsl(var(--info))' }}>
                <div className="p-5">
                  {/* Member Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: 'hsl(var(--info))', color: 'white' }}>
                      {member.person.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold">{member.person.name}</h3>
                      <p className="text-xs text-muted-foreground">{member.person.position_title} &middot; {member.person.primary_role}</p>
                    </div>
                    {member.person.start_date && (
                      <span className="text-xs text-muted-foreground">Starts {new Date(member.person.start_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    )}
                  </div>

                  {/* Progress */}
                  {tp && (
                    <div className="mb-4">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: 'hsl(var(--success))' }} />
                        </div>
                        <span className="text-sm font-semibold text-[hsl(var(--success))]">{tp.complete}/{tp.total}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">activities signed off</p>
                    </div>
                  )}

                  {/* Sign-off Toggles */}
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Training Sign-off</p>
                  <div className="space-y-2">
                    {member.activities.map(act => {
                      const isComplete = act.training_status === 'Complete';
                      const statusLabel = act.training_status || 'Not Started';
                      return (
                        <div key={act.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 transition-colors">
                          <button
                            onClick={() => handleTrainingToggle(act.id, act.training_status)}
                            className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                              isComplete ? 'bg-[hsl(var(--success))] border-[hsl(var(--success))] text-white' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]'
                            }`}
                            data-testid="team-training-toggle"
                            aria-label={`Mark ${act.name} as ${isComplete ? 'not started' : 'complete'}`}
                          >
                            {isComplete && <Check size={14} />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${isComplete ? 'line-through opacity-50' : ''}`}>{act.name}</p>
                            {act.primary_procedures?.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Guided by: {act.primary_procedures.map(p => p.name).join(', ')}
                              </p>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${isComplete ? 'badge-active' : 'badge-draft'}`}>
                            {statusLabel}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* Onboarding Journey (for CCR with Onboarding status) — collapsible */}
      {is_onboarding && onboarding && (
        <section className="mb-8" data-testid="onboarding-section">
          <div className="ps-card-static overflow-hidden" style={{ borderLeft: '4px solid hsl(var(--info))' }}>
            {/* Onboarding Header — clickable to collapse */}
            <button onClick={() => toggleSection('onboarding')} className="w-full p-5 pb-4 flex items-center text-left">
              <div className="flex items-center gap-2 flex-1">
                <GraduationCap size={20} className="text-[hsl(var(--info))]" />
                <h2 className="text-lg font-bold">Your Onboarding Journey</h2>
                {onboarding.start_date && (
                  <span className="text-xs text-muted-foreground ml-auto mr-3">
                    Start date: {new Date(onboarding.start_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                )}
              </div>
              <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-200 ${collapsed.onboarding ? '' : 'rotate-180'}`} />
            </button>
            {!collapsed.onboarding && (
            <>
              <div className="px-5 pb-4">
              {/* Progress Bar */}
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(onboarding.training_progress.complete / Math.max(onboarding.training_progress.total, 1)) * 100}%`, background: 'hsl(var(--success))' }} />
                </div>
                <span className="text-sm font-semibold text-[hsl(var(--success))]">
                  {onboarding.training_progress.complete}/{onboarding.training_progress.total}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">activities signed off by your trainer</p>
              
              {/* Welcome Letter Button */}
              {onboarding.welcome_content && onboarding.welcome_content.length > 0 && (
                <button onClick={(e) => { e.stopPropagation(); setShowWelcome(!showWelcome); }} className="mt-3 flex items-center gap-2 text-sm text-primary font-medium hover:underline" data-testid="welcome-letter-toggle">
                  <Heart size={14} /> {showWelcome ? 'Hide' : 'Read'} Welcome Letter
                </button>
              )}
            </div>
            
            {/* Welcome Letter Content */}
            {showWelcome && onboarding.welcome_content && (
              <div className="px-5 pb-5">
                <div className="bg-[hsl(36_33%_94%)] rounded-xl p-5 italic text-sm leading-relaxed">
                  <p className="mb-4 not-italic font-semibold text-base" style={{ color: 'hsl(var(--foreground))' }}>Dear {person.name.split(' ')[0]},</p>
                  <NotionBlockRenderer blocks={onboarding.welcome_content} />
                </div>
              </div>
            )}
            
            {/* Phases Timeline */}
            <div className="px-5 pb-5">
              <div className="space-y-2">
                {onboarding.phases.map((phase, idx) => {
                  const phaseStatus = phase.status || 'not_started';
                  const isSignOff = idx === onboarding.phases.length - 1;
                  const phaseNum = idx + 1;
                  const isVerified = phase.verified || false;
                  
                  return (
                  <div key={idx} className="rounded-xl border border-border overflow-hidden">
                    <div className="flex items-center gap-3 p-3">
                      {/* Phase verify toggle (Director/Ops only, not for Phase 5) */}
                      {canWrite && !isSignOff ? (
                        <button
                          onClick={() => handlePhaseToggle(phaseNum, isVerified)}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 transition-colors ${
                            phaseStatus === 'complete' ? 'bg-[hsl(var(--success))] text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                          title={phaseStatus === 'complete' ? 'Click to unverify' : 'Click to verify as complete'}
                        >
                          {phaseStatus === 'complete' ? <Check size={14} /> : phaseNum}
                        </button>
                      ) : (
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                          phaseStatus === 'complete' ? 'bg-[hsl(var(--success))] text-white' :
                          phaseStatus === 'in_progress' ? 'bg-[hsl(var(--info))] text-white' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {phaseStatus === 'complete' ? <Check size={14} /> : phaseNum}
                        </div>
                      )}
                      <button
                        onClick={() => setExpandedPhase(expandedPhase === idx ? null : idx)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <p className="text-sm font-semibold">{phase.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {phase.day_info}
                          {isSignOff && ` \u2014 ${onboarding.training_progress.complete}/${onboarding.training_progress.total} activities signed off`}
                        </p>
                      </button>
                      <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                        phaseStatus === 'complete' ? 'badge-active' :
                        phaseStatus === 'in_progress' ? 'badge-improving' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {phaseStatus === 'complete' ? (canWrite && !isSignOff ? 'Verified' : 'Complete') : phaseStatus === 'in_progress' ? 'In Progress' : 'Not verified'}
                      </span>
                      <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-200 ${expandedPhase === idx ? 'rotate-180' : ''}`} />
                    </div>
                    {expandedPhase === idx && (
                      <div className="px-4 pb-4 border-t border-border bg-muted/10">
                        <div className="pt-3 space-y-1.5">
                          {phase.checklists.length > 0 && phase.checklists.map((item, ci) => (
                            <p key={ci} className="text-xs leading-relaxed">{item}</p>
                          ))}
                          {phase.content.map((item, ci) => (
                            <p key={ci} className="text-xs text-muted-foreground leading-relaxed">{item}</p>
                          ))}
                          {/* Show sign-off activities in Phase 5 */}
                          {isSignOff && (
                            <div className="mt-3 pt-3 border-t border-border">
                              <p className="text-xs font-semibold text-muted-foreground mb-2">Your trainer will sign off each activity when you demonstrate readiness:</p>
                              {onboarding.training_progress.activities.map(act => (
                                <div key={act.id} className="flex items-center gap-2 text-xs py-1">
                                  <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${
                                    act.training_status === 'Complete'
                                      ? 'bg-[hsl(var(--success))] text-white'
                                      : 'border border-[hsl(var(--border))]'
                                  }`}>
                                    {act.training_status === 'Complete' && <Check size={10} />}
                                  </span>
                                  <span className={act.training_status === 'Complete' ? 'line-through opacity-50' : ''}>{act.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
            
            {/* Training Sign-off Overview */}
            <div className="px-5 pb-5 border-t border-border pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Target size={14} className="text-muted-foreground" />
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Phase 5 — Training Sign-off</p>
              </div>
              <p className="text-xs text-muted-foreground mb-3">These are signed off by your trainer (Operations Manager) when you demonstrate competency. Each maps to a procedure you'll learn during your onboarding.</p>
              <div className="space-y-2">
                {onboarding.training_progress.activities.map(act => (
                  <div key={act.id} className="flex items-center gap-3 text-sm">
                    <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                      act.training_status === 'Complete'
                        ? 'bg-[hsl(var(--success))] border-[hsl(var(--success))] text-white'
                        : 'border-[hsl(var(--border))] opacity-50'
                    }`}>
                      {act.training_status === 'Complete' && <Check size={12} />}
                    </span>
                    <span className={act.training_status === 'Complete' ? 'line-through opacity-50' : ''}>{act.name}</span>
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
                      act.training_status === 'Complete' ? 'badge-active' : 'badge-draft'
                    }`}>
                      {act.training_status || 'Awaiting sign-off'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            </>
            )}
          </div>
        </section>
      )}

      {/* CRM Pipeline — all roles, collapsible */}
      {crm_pipeline && (
        <section className="mb-8">
          <button onClick={() => toggleSection('pipeline')} className="flex items-center justify-between w-full mb-4 text-left">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">Operations Pipeline</h2>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'hsl(185 40% 93%)', color: 'hsl(185 55% 30%)' }}>Live from CRM</span>
            </div>
            <div className="flex items-center gap-2">
              {crmKpis && <span className="text-xs text-muted-foreground">{crmKpis.active_cases} active</span>}
              <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-200 ${collapsed.pipeline ? '' : 'rotate-180'}`} />
            </div>
          </button>
          {!collapsed.pipeline && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { key: 'Not Yet in Our Care', label: 'Awaiting', color: 'hsl(220 10% 92%)' },
                { key: 'Refrigeration', label: 'Refrigeration', color: 'hsl(205 50% 92%)' },
                { key: 'Pet 400', label: 'Aquacremation', color: 'hsl(185 40% 92%)' },
                { key: 'Dehydrator', label: 'Dehydrator', color: 'hsl(38 70% 92%)' },
                { key: 'Urn Assembly', label: 'Urn Assembly', color: 'hsl(270 30% 92%)' },
                { key: 'Ready for Collection', label: 'Ready', color: 'hsl(142 40% 92%)' },
              ].map(stage => (
                <div key={stage.key} className="ps-card-static p-4 text-center" style={{ background: stage.color }}>
                  <p className="text-2xl font-bold">{crm_pipeline[stage.key] || 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">{stage.label}</p>
                </div>
              ))}
            </div>
          )}
          {!collapsed.pipeline && crmKpis && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
              <div className="ps-card-static p-3">
                <p className="text-lg font-bold">{crmKpis.new_enquiries_week}</p>
                <p className="text-xs text-muted-foreground">New this week</p>
              </div>
              <div className="ps-card-static p-3">
                <p className="text-lg font-bold">${(crmKpis.revenue_month || 0).toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Revenue this month</p>
              </div>
              <div className="ps-card-static p-3">
                <p className="text-lg font-bold">{crmKpis.in_care_sms_pct}%</p>
                <p className="text-xs text-muted-foreground">In Care SMS sent</p>
              </div>
              <div className="ps-card-static p-3">
                <p className="text-lg font-bold">{crmKpis.payment_before_collection_pct}%</p>
                <p className="text-xs text-muted-foreground">Payment collected</p>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Pet 400 Production Card — collapsible */}
      {pet400 && pet400.cycles_completed > 0 && (
        <section className="mb-8">
          <button onClick={() => toggleSection('pet400')} className="flex items-center justify-between w-full mb-4 text-left">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">Production</h2>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'hsl(185 40% 93%)', color: 'hsl(185 55% 30%)' }}>Live from CRM</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{pet400.total_pets} pets this month</span>
              <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-200 ${collapsed.pet400 ? '' : 'rotate-180'}`} />
            </div>
          </button>
          {!collapsed.pet400 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div className="ps-card-static p-4 text-center">
                <p className="text-2xl font-bold">{pet400.cycles_completed}</p>
                <p className="text-xs text-muted-foreground mt-1">Cycles</p>
              </div>
              <div className="ps-card-static p-4 text-center">
                <p className="text-2xl font-bold">{pet400.total_pets}</p>
                <p className="text-xs text-muted-foreground mt-1">Pets processed</p>
              </div>
              <div className="ps-card-static p-4 text-center">
                <p className="text-2xl font-bold">{pet400.avg_per_cycle}</p>
                <p className="text-xs text-muted-foreground mt-1">Avg per cycle</p>
              </div>
              <div className="ps-card-static p-4 text-center">
                <p className="text-2xl font-bold">{pet400.capacity_pct}%</p>
                <p className="text-xs text-muted-foreground mt-1">Capacity</p>
              </div>
              <div className="ps-card-static p-4 text-center">
                <p className="text-2xl font-bold">${pet400.avg_cost}</p>
                <p className="text-xs text-muted-foreground mt-1">Cost/pet</p>
              </div>
            </div>
          )}
        </section>
      )}

      {/* KPI Snapshot — collapsible */}
      {kpis.length > 0 && (
        <section className="mb-8">
          <button onClick={() => toggleSection('kpis')} className="flex items-center justify-between w-full mb-4 text-left">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">KPI Snapshot</h2>
              {!canWrite && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">View only</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Link to="/kpis" className="text-sm text-primary font-medium flex items-center gap-1 hover:underline" onClick={e => e.stopPropagation()}>View all <ChevronRight size={14} /></Link>
              <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-200 ${collapsed.kpis ? '' : 'rotate-180'}`} />
            </div>
          </button>
          {!collapsed.kpis && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {kpis.slice(0, 6).map(kpi => {
              const status = getKpiStatus(kpi.current_value, kpi.target);
              return (
                <div key={kpi.id} className={`ps-card-static p-5 ${status.cls}`} data-testid="home-kpi-snapshot-card">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">{kpi.category}</p>
                      <p className="text-sm font-semibold mb-3">{kpi.name}</p>
                    </div>
                    {canWrite && (
                      <button onClick={() => { setEditingKpi(kpi.id); setEditValue(kpi.current_value ?? ''); }} className="p-1.5 rounded-lg hover:bg-muted/50 transition-colors" data-testid="kpi-inline-edit-button">
                        <Pencil size={14} className="text-muted-foreground" />
                      </button>
                    )}
                  </div>
                  {editingKpi === kpi.id ? (
                    <div className="flex items-center gap-2">
                      <input type="number" value={editValue} onChange={e => setEditValue(e.target.value)} className="w-24 px-3 py-2 text-2xl font-bold rounded-lg border border-input bg-background" autoFocus data-testid="kpi-inline-edit-input" onKeyDown={e => { if (e.key === 'Enter') handleKpiUpdate(kpi.id, editValue); if (e.key === 'Escape') setEditingKpi(null); }} />
                      <button onClick={() => handleKpiUpdate(kpi.id, editValue)} className="p-2 rounded-lg bg-primary text-primary-foreground" data-testid="kpi-inline-edit-save-button"><Check size={16} /></button>
                      <button onClick={() => setEditingKpi(null)} className="p-2 rounded-lg hover:bg-muted" data-testid="kpi-inline-edit-cancel-button"><X size={16} /></button>
                    </div>
                  ) : (
                    <div>
                      <span className="text-3xl font-bold tracking-tight">{kpi.current_value !== null && kpi.current_value !== undefined ? kpi.current_value : '\u2014'}</span>
                      <span className="text-sm text-muted-foreground ml-2">/ {kpi.target ?? '\u2014'}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${status.color === 'green' ? 'badge-active' : status.color === 'orange' ? 'badge-draft' : status.color === 'red' ? 'badge-critical' : 'bg-muted text-muted-foreground'}`}>{status.label}</span>
                    <span className="text-xs text-muted-foreground">{kpi.review_frequency}</span>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </section>
      )}

      {/* My Activities — collapsible */}
      <section className="mb-8">
        <button onClick={() => toggleSection('activities')} className="flex items-center justify-between w-full mb-4 text-left">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">My Activities</h2>
            {!canWrite && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">View only</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{activities.filter(a => a.training_status === 'Complete').length}/{activities.length} complete</span>
            <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-200 ${collapsed.activities ? '' : 'rotate-180'}`} />
          </div>
        </button>
        {!collapsed.activities && (
        <>
        {activities.length > 0 ? (
          <div className="space-y-2">
            {activities.map(act => {
              const isComplete = act.training_status === 'Complete';
              const statusLabel = act.training_status || 'Not Started';
              const isExpanded = expandedActivity === act.id;
              const hasProcedures = (act.primary_procedures && act.primary_procedures.length > 0) || (act.supporting_procedures && act.supporting_procedures.length > 0);
              
              return (
                <div key={act.id} className="ps-card-static overflow-hidden" data-testid="home-activity-item">
                  <div className="p-4 flex items-center gap-4">
                    {canWrite ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleTrainingToggle(act.id, act.training_status); }}
                        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                          isComplete ? 'bg-[hsl(var(--success))] border-[hsl(var(--success))] text-white' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]'
                        }`}
                        data-testid="activity-training-toggle"
                        aria-label={`Mark ${act.name} as ${isComplete ? 'not started' : 'complete'}`}
                      >
                        {isComplete && <Check size={14} />}
                      </button>
                    ) : (
                      <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                        isComplete ? 'bg-[hsl(var(--success))] border-[hsl(var(--success))] text-white' : 'border-[hsl(var(--border))] opacity-50'
                      }`} title="View only — Director/Ops Manager can update">
                        {isComplete && <Check size={14} />}
                      </span>
                    )}
                    <button onClick={() => setExpandedActivity(isExpanded ? null : act.id)} className="flex-1 min-w-0 text-left">
                      <p className={`text-sm font-medium ${isComplete ? 'line-through opacity-60' : ''}`}>{act.name}</p>
                      <p className="text-xs text-muted-foreground">{act.owning_role}</p>
                    </button>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        isComplete ? 'badge-active' : statusLabel === 'In Progress' ? 'badge-improving' : 'badge-draft'
                      }`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60" />
                        {statusLabel}
                      </span>
                      {hasProcedures && (
                        <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Procedure Bridge */}
                  {isExpanded && hasProcedures && (
                    <div className="px-4 pb-4 border-t border-border bg-muted/10 pt-3">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">How to do this</p>
                      {act.primary_procedures?.map(proc => (
                        <button key={proc.id} onClick={() => openProcedure(proc.id)} className="flex items-start gap-3 p-3 rounded-xl bg-card hover:bg-accent/30 transition-colors mb-2 w-full text-left" data-testid="activity-procedure-link">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'hsl(var(--primary) / 0.1)' }}>
                            <FileText size={16} className="text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold">{proc.name || 'Linked Procedure'}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <TriggerPill trigger={proc.trigger} />
                              <TypePill type={proc.procedure_type} />
                            </div>
                          </div>
                          <ArrowRight size={16} className="text-muted-foreground mt-1" />
                        </button>
                      ))}
                      {act.supporting_procedures?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-muted-foreground mb-1">Supporting references:</p>
                          {act.supporting_procedures.map(proc => (
                            <button key={proc.id} onClick={() => openProcedure(proc.id)} className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-left">
                              <BookOpen size={12} />
                              <span>{proc.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState icon={Activity} title="No activities assigned" description="Activities assigned to your role will appear here." />
        )}
        </>
        )}
      </section>

      {/* My Procedures — collapsible */}
      {procedures.length > 0 && (
        <section className="mb-8">
          <button onClick={() => toggleSection('procedures')} className="flex items-center justify-between w-full mb-4 text-left">
            <h2 className="text-lg font-semibold">My Procedures</h2>
            <div className="flex items-center gap-2">
              <Link to="/procedures" className="text-sm text-primary font-medium flex items-center gap-1 hover:underline" onClick={e => e.stopPropagation()}>View all <ChevronRight size={14} /></Link>
              <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-200 ${collapsed.procedures ? '' : 'rotate-180'}`} />
            </div>
          </button>
          {!collapsed.procedures && (
          <div className="space-y-2">
            {procedures.map(proc => (
              <button
                key={proc.id}
                onClick={() => openProcedure(proc.id)}
                className="w-full text-left ps-card p-4 flex items-start gap-4"
                data-testid="home-procedure-card"
              >
                <span className="font-mono text-xs bg-muted px-2.5 py-1 rounded-full font-semibold flex-shrink-0 mt-0.5">
                  {proc.name.match(/^P\d+/)?.[0] || 'P??'}
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold">{proc.name.replace(/^P\d+\s*[\u2013\u2014-]\s*/, '').trim() || proc.name}</h3>
                  {proc.purpose && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{proc.purpose}</p>}
                </div>
                <ArrowRight size={16} className="text-muted-foreground mt-1 flex-shrink-0" />
              </button>
            ))}
          </div>
          )}
        </section>
      )}

      {/* Quick Actions — collapsible */}
      <section className="mb-8">
        <button onClick={() => toggleSection('quickActions')} className="flex items-center justify-between w-full mb-4 text-left">
          <h2 className="text-lg font-semibold">Quick Actions</h2>
          <ChevronDown size={16} className={`text-muted-foreground transition-transform duration-200 ${collapsed.quickActions ? '' : 'rotate-180'}`} />
        </button>
        {!collapsed.quickActions && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/reference" className="ps-card p-4 flex items-center gap-3" data-testid="quick-action-reference">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'hsl(var(--accent))' }}><BookOpen size={20} className="text-primary" /></div>
            <div><p className="text-sm font-semibold">Reference Docs</p><p className="text-xs text-muted-foreground">Manuals & guides</p></div>
          </Link>
          <Link to="/incidents" className="ps-card p-4 flex items-center gap-3" data-testid="quick-action-incidents">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'hsl(0 80% 94%)' }}><AlertTriangle size={20} style={{ color: 'hsl(0, 72%, 34%)' }} /></div>
            <div><p className="text-sm font-semibold">Incident Register</p><p className="text-xs text-muted-foreground">{canWrite ? 'Log & review' : 'View incidents'}</p></div>
          </Link>
          <Link to="/kpis" className="ps-card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'hsl(142 40% 92%)' }}><TrendingUp size={20} style={{ color: 'hsl(142, 34%, 34%)' }} /></div>
            <div><p className="text-sm font-semibold">KPI Dashboard</p><p className="text-xs text-muted-foreground">Track performance</p></div>
          </Link>
          <Link to="/vision" className="ps-card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'hsl(var(--accent))' }}><Zap size={20} className="text-primary" /></div>
            <div><p className="text-sm font-semibold">Vision & Values</p><p className="text-xs text-muted-foreground">Our purpose</p></div>
          </Link>
        </div>
        )}
      </section>

      {/* Procedure Side Panel */}
      {selectedProcId && (
        <>
          <div className={`side-panel-overlay ${selectedProcId ? 'open' : ''}`} onClick={() => setSelectedProcId(null)} />
          <div className={`side-panel ${selectedProcId ? 'open' : ''}`} data-testid="procedure-detail-panel">
            <div className="sticky top-0 bg-card z-10 p-6 border-b border-border">
              <button onClick={() => setSelectedProcId(null)} className="p-2 rounded-lg hover:bg-muted" data-testid="panel-close-button">
                <ChevronLeft size={20} />
              </button>
            </div>
            <div className="p-6">
              {procDetailLoading ? (
                <div className="space-y-3">
                  {[1,2,3,4,5].map(i => <div key={i} className="h-4 bg-muted rounded animate-skeleton" style={{ width: `${60 + Math.random() * 40}%` }} />)}
                </div>
              ) : procDetail ? (
                procDetail.guided ? (
                  <GuidedProcedureView guided={procDetail.guided} blocks={procDetail.blocks} />
                ) : (
                  <NotionBlockRenderer blocks={procDetail.blocks} />
                )
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
